import {
  APP_BASE_HREF,
  CommonModule,
  DatePipe,
  PlatformLocation,
} from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTreeModule } from '@angular/material/tree';
import { EditorModule } from '@tinymce/tinymce-angular';
import { AgGridModule } from 'ag-grid-angular';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { TagInputModule } from 'ngx-chips';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';

import { CustomDatePipe } from '../_pipes/custom-date.pipe';
import { DateTimePipe } from '../_pipes/date-time.pipe';
import { FileSizePipe } from '../_pipes/file-size.pipe';
import { customerFilter, SplitPipe } from '../_pipes/filter.pipe';
import { ReplaceUnderscorePipe } from '../_pipes/replace-underscore-pipe';
import { SafeHtmlPipe } from '../safehtml.pipe';
import { FileDragDropDirective } from './_directives/file-drag-drop.directive';
import { ActionModalComponent } from './components/action-modal/action-modal.component';
import { AddClaimModalComponent } from './components/add-claim-modal/add-claim-modal.component';
import { AnimalClaimInfoComponent } from './components/add-claim-modal/animal-claim-info/animal-claim-info.component';
import { BuildingClaimInfoComponent } from './components/add-claim-modal/building-claim-info/building-claim-info.component';
import { BusinessAccidentClaimInfoComponent } from './components/add-claim-modal/business-accident-claim-info/business-accident-claim-info.component';
import { BusinessCompanyRcClaimInfoComponent } from './components/add-claim-modal/business-company-rc-claim-info/business-company-rc-claim-info.component';
import { BusinessCompanyThingsClaimInfoComponent } from './components/add-claim-modal/business-company-things-claim-info/business-company-things-claim-info.component';
import { BusinessGenericClaimInfoComponent } from './components/add-claim-modal/business-generic-claim-info/business-generic-claim-info.component';
import { BusinessHealthClaimInfoComponent } from './components/add-claim-modal/business-health-claim-info/business-health-claim-info.component';
import { ClaimCircumstancesComponent } from './components/add-claim-modal/components/claim-circumstances/claim-circumstances.component';
import { ConstructionClaimInfoComponent } from './components/add-claim-modal/construction-claim-info/construction-claim-info.component';
import { HouseholdClaimInfoComponent } from './components/add-claim-modal/household-claim-info/household-claim-info.component';
import { LegalProtectionClaimInfoComponent } from './components/add-claim-modal/legal-protection-claim-info/legal-protection-claim-info.component';
import { TravelClaimInfoComponent } from './components/add-claim-modal/travel-claim-info/travel-claim-info.component';
import { ValuableObjectsClaimInfoComponent } from './components/add-claim-modal/valuable-objects-claim-info/valuable-objects-claim-info.component';
import { VehicleClaimInfoComponent } from './components/add-claim-modal/vehicle-claim-info/vehicle-claim-info.component';
import { AddLeadModalComponent } from './components/add-lead-modal/add-lead-modal.component';
import { AddProposalModalComponent } from './components/add-proposal-modal/add-proposal-modal.component';
import { ProductSelectionComponent } from './components/add-proposal-modal/product-selection/product-selection.component';
import { IconRendererComponent } from './components/ag-grid-renderers/icon-renderer/icon-renderer.component';
import { StackedDateRendererComponent } from './components/ag-grid-renderers/stacked-date-renderer/stacked-date-renderer.component';
import { StackedTextRendererComponent } from './components/ag-grid-renderers/stacked-text-renderer/stacked-text-renderer.component';
import { AgentActivationModalComponent } from './components/agent-activation-modal/agent-activation-modal.component';
import { AssignProductsModalComponent } from './components/assign-products-modal/assign-products-modal.component';
import { BaseLayoutComponent } from './components/base-layout/base-layout.component';
import { BarChartComponent } from './components/charts/bar-chart/bar-chart.component';
import { BarDistributionChartComponent } from './components/charts/bar-distribution-chart/bar-distribution-chart.component';
import { DonutChartComponent } from './components/charts/donut-chart/donut-chart.component';
import { SpeedometerChartComponent } from './components/charts/speedometer-chart/speedometer-chart.component';
import { BusinessAccidentClaimDetailsComponent } from './components/claim-details/business-claim-details/business-accident-claim-details/business-accident-claim-details.component';
import { BusinessCompanyRcClaimDetailsComponent } from './components/claim-details/business-claim-details/business-company-rc-claim-details/business-company-rc-claim-details.component';
import { BusinessCompanyThingsClaimDetailsComponent } from './components/claim-details/business-claim-details/business-company-things-claim-details/business-company-things-claim-details.component';
import { BusinessGenericClaimDetailsComponent } from './components/claim-details/business-claim-details/business-generic-claim-details/business-generic-claim-details.component';
import { BusinessHealthClaimDetailsComponent } from './components/claim-details/business-claim-details/business-health-claim-details/business-health-claim-details.component';
import { ClaimDetailsComponent } from './components/claim-details/claim-details.component';
import { PersonalClaimDetailsComponent } from './components/claim-details/personal-claim-details/personal-claim-details.component';
import { CollaboratorFormComponent } from './components/collaborator-form/collaborator-form.component';
import { ComboboxComponent } from './components/combobox/combobox.component';
import { CommissionBaseListComponent } from './components/commission-base-list/commission-base-list.component';
import { CommissionListButtonComponent } from './components/commission-base-list/commission-list-button/commission-list-button.component';
import { ConvertToCollaboratorModalComponent } from './components/convert-to-collaborator-modal/convert-to-collaborator-modal.component';
import { CustomerNavComponent } from './components/customer-nav/customer-nav.component';
import { CustomerSourceSelectComponent } from './components/customer-source-select/customer-source-select.component';
import { DataAnalysisNavComponent } from './components/data-analysis-nav/data-analysis-nav.component';
import { DocumentRequestModalComponent } from './components/document-request-modal/document-request-modal.component';
import { DocumentUploadModalComponent } from './components/document-upload-modal/document-upload-modal.component';
import { DuplicateLeadComponent } from './components/duplicate-lead/duplicate-lead.component';
import { FileInputComponent } from './components/file-input/file-input.component';
import { FilterTabsComponent } from './components/filter-tabs/filter-tabs.component';
import { FloatingButtonsComponent } from './components/floating-buttons/floating-buttons.component';
import { GenerateUserPasswordModalComponent } from './components/generate-user-password-modal/generate-user-password-modal.component';
import { GoogleAutocompleteComponent } from './components/google-autocomplete/google-autocomplete.component';
import { GridCopyButtonComponent } from './components/grid-copy-button/grid-copy-button.component';
import { LanguageSwitcherComponent } from './components/language-switcher/language-switcher.component';
import { LeadDetailsComponent } from './components/lead-details/lead-details.component';
import { AnimalMetadataComponent } from './components/lead-details/metadata-components/animal-metadata/animal-metadata.component';
import { BuildingMetadataComponent } from './components/lead-details/metadata-components/building-metadata/building-metadata.component';
import { BusinessBasicMetadataComponent } from './components/lead-details/metadata-components/business-basic-metadata/business-basic-metadata.component';
import { BusinessAccidentMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-accident-metadata/business-accident-metadata.component';
import { BusinessCyberMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-cyber-metadata/business-cyber-metadata.component';
import { BusinessFleetMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-fleet-metadata/business-fleet-metadata.component';
import { BusinessHealthMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-health-metadata/business-health-metadata.component';
import { BusinessLegalProtectionMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-legal-protection-metadata/business-legal-protection-metadata.component';
import { BusinessRetirementMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-retirement-metadata/business-retirement-metadata.component';
import { BusinessTechnicalInstallationMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-technical-installation-metadata/business-technical-installation-metadata.component';
import { BusinessThingsMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-things-metadata/business-things-metadata.component';
import { BusinessTransportMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-transport-metadata/business-transport-metadata.component';
import { BusinessVehicleMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/business-vehicle-metadata/business-vehicle-metadata.component';
import { RcCompanyMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/rc-company-metadata/rc-company-metadata.component';
import { SharedBusinessMetadataComponent } from './components/lead-details/metadata-components/business-metadatas/shared-business-metadata/shared-business-metadata.component';
import { CancellationMetadataComponent } from './components/lead-details/metadata-components/cancellation-metadata/cancellation-metadata.component';
import { CentralisationMetadataComponent } from './components/lead-details/metadata-components/centralisation-metadata/centralisation-metadata.component';
import { CollaboratorMetadataComponent } from './components/lead-details/metadata-components/collaborator-metadata/collaborator-metadata.component';
import { ConstructionMetadataComponent } from './components/lead-details/metadata-components/construction-metadata/construction-metadata.component';
import { HealthMetadataComponent } from './components/lead-details/metadata-components/health-metadata/health-metadata.component';
import { HouseholdMetadataComponent } from './components/lead-details/metadata-components/household-metadata/household-metadata.component';
import { LegalProtectionMetadataComponent } from './components/lead-details/metadata-components/legal-protection-metadata/legal-protection-metadata.component';
import { PolicyUpdateMetadataComponent } from './components/lead-details/metadata-components/policy-update-metadata/policy-update-metadata.component';
import { ThirdPillarMetadataComponent } from './components/lead-details/metadata-components/third-pillar-metadata/third-pillar-metadata.component';
import { TravelMetadataComponent } from './components/lead-details/metadata-components/travel-metadata/travel-metadata.component';
import { ValuableObjectsMetadataComponent } from './components/lead-details/metadata-components/valuable-objects-metadata/valuable-objects-metadata.component';
import { VehicleMetadataComponent } from './components/lead-details/metadata-components/vehicle-metadata/vehicle-metadata.component';
import { AnimalFormComponent } from './components/lead-form/animal-form/animal-form.component';
import { BenefitsAccountFormComponent } from './components/lead-form/benefits-account-form/benefits-account-form.component';
import { BuildingFormComponent } from './components/lead-form/building-form/building-form.component';
import { BusinessAccidentFormComponent } from './components/lead-form/business-forms/business-accident-form/business-accident-form.component';
import { BusinessConstructionFormComponent } from './components/lead-form/business-forms/business-construction-form/business-construction-form.component';
import { BusinessConstructionWarrantyFormComponent } from './components/lead-form/business-forms/business-construction-warranty-form/business-construction-warranty-form.component';
import { BusinessCyberFormComponent } from './components/lead-form/business-forms/business-cyber-form/business-cyber-form.component';
import { BusinessFleetFormComponent } from './components/lead-form/business-forms/business-fleet-form/business-fleet-form.component';
import { BusinessHealthFormComponent } from './components/lead-form/business-forms/business-health-form/business-health-form.component';
import { BusinessLegalProtectionFormComponent } from './components/lead-form/business-forms/business-legal-protection-form/business-legal-protection-form.component';
import { BusinessRentalFormComponent } from './components/lead-form/business-forms/business-rental-form/business-rental-form.component';
import { BusinessRetirementFormComponent } from './components/lead-form/business-forms/business-retirement-form/business-retirement-form.component';
import { BusinessTechnicalInstallationFormComponent } from './components/lead-form/business-forms/business-technical-installation-form/business-technical-installation-form.component';
import { BusinessThingsFormComponent } from './components/lead-form/business-forms/business-things-form/business-things-form.component';
import { BusinessTransportFormComponent } from './components/lead-form/business-forms/business-transport-form/business-transport-form.component';
import { BusinessVehicleFormComponent } from './components/lead-form/business-forms/business-vehicle-form/business-vehicle-form.component';
import { RcCompanyFormComponent } from './components/lead-form/business-forms/rc-company-form/rc-company-form.component';
import { SharedBusinessFormComponent } from './components/lead-form/business-forms/shared-business-form/shared-business-form.component';
import { CentralisationFormComponent } from './components/lead-form/centralisation-form/centralisation-form.component';
import { ConstructionFormComponent } from './components/lead-form/construction-form/construction-form.component';
import { HealthFormComponent } from './components/lead-form/health-form/health-form.component';
import { HousingRentalFormComponent } from './components/lead-form/housing-rental-form/housing-rental-form.component';
import { LeadFormComponent } from './components/lead-form/lead-form.component';
import { LegalProtectionFormComponent } from './components/lead-form/legal-protection-form/legal-protection-form.component';
import { RcHouseholdFormComponent } from './components/lead-form/rc-household-form/rc-household-form.component';
import { ThirdPillarFormComponent } from './components/lead-form/third-pillar-form/third-pillar-form.component';
import { TravelFormComponent } from './components/lead-form/travel-form/travel-form.component';
import { ValuableObjectsFormComponent } from './components/lead-form/valuable-objects-form/valuable-objects-form.component';
import { VehicleFormComponent } from './components/lead-form/vehicle-form/vehicle-form.component';
import { LinearStepperComponent } from './components/linear-stepper/linear-stepper.component';
import { PdfViewerModalComponent } from './components/pdf-viewer-modal/pdf-viewer-modal.component';
import { ProposalDetailsComponent } from './components/proposal-details/proposal-details.component';
import { SelectAllCheckboxComponent } from './components/select-all-checkbox/select-all-checkbox.component';
import { SelectAllMatCheckboxComponent } from './components/select-all-mat-checkbox/select-all-mat-checkbox.component';
import { SlideInModalComponent } from './components/slide-in-modal/slide-in-modal.component';
import { SlideUpModalComponent } from './components/slide-up-modal/slide-up-modal.component';
import { TabNavigationComponent } from './components/tab-navigation/tab-navigation.component';
import { FlowCollapseButtonComponent } from './components/tasks-list/flow-collapse-button/flow-collapse-button.component';
import { TasksListComponent } from './components/tasks-list/tasks-list.component';
import { ToggleComponent } from './components/toggle/toggle.component';
import { TreeComponent } from './components/tree/tree.component';
import { UpdateDocumentModalComponent } from './components/update-document-modal/update-document-modal.component';
import { ProposalProductsDisplayerComponent } from './components/view-proposals/proposal-products-displayer/proposal-products-displayer.component';
import { ProposalsActionsComponent } from './components/view-proposals/proposals-actions/proposals-actions.component';
import { SendProposalsModalComponent } from './components/view-proposals/send-proposals-modal/send-proposals-modal.component';
import { ViewProposalsComponent } from './components/view-proposals/view-proposals.component';
import { IconsModule } from './icons/icons.module';
import { SharedRoutingModule } from './shared-routing.module';

export function getBaseHref(platformLocation: PlatformLocation): string {
  return platformLocation.getBaseHrefFromDOM();
}

@NgModule({
  declarations: [
    AddClaimModalComponent,
    AgentActivationModalComponent,
    AnimalClaimInfoComponent,
    AnimalMetadataComponent,
    AssignProductsModalComponent,
    BarDistributionChartComponent,
    BuildingClaimInfoComponent,
    BuildingMetadataComponent,
    BusinessAccidentClaimDetailsComponent,
    BusinessAccidentClaimInfoComponent,
    BusinessAccidentMetadataComponent,
    BusinessBasicMetadataComponent,
    BusinessCompanyRcClaimDetailsComponent,
    BusinessCompanyRcClaimInfoComponent,
    BusinessCompanyThingsClaimDetailsComponent,
    BusinessCompanyThingsClaimInfoComponent,
    BusinessCyberMetadataComponent,
    BusinessFleetMetadataComponent,
    BusinessGenericClaimDetailsComponent,
    BusinessGenericClaimInfoComponent,
    BusinessHealthClaimDetailsComponent,
    BusinessHealthClaimInfoComponent,
    BusinessHealthMetadataComponent,
    BusinessLegalProtectionMetadataComponent,
    BusinessRetirementMetadataComponent,
    BusinessTechnicalInstallationMetadataComponent,
    BusinessThingsMetadataComponent,
    BusinessTransportMetadataComponent,
    BusinessVehicleMetadataComponent,
    CancellationMetadataComponent,
    CentralisationMetadataComponent,
    ClaimCircumstancesComponent,
    ClaimDetailsComponent,
    CollaboratorFormComponent,
    CollaboratorMetadataComponent,
    CommissionBaseListComponent,
    CommissionListButtonComponent,
    ConstructionClaimInfoComponent,
    ConstructionMetadataComponent,
    ConvertToCollaboratorModalComponent,
    customerFilter,
    CustomerNavComponent,
    CustomerSourceSelectComponent,
    DataAnalysisNavComponent,
    DateTimePipe,
    DocumentRequestModalComponent,
    DonutChartComponent,
    DuplicateLeadComponent,
    DuplicateLeadComponent,
    FileDragDropDirective,
    FloatingButtonsComponent,
    FlowCollapseButtonComponent,
    GenerateUserPasswordModalComponent,
    GoogleAutocompleteComponent,
    GridCopyButtonComponent,
    HealthMetadataComponent,
    HouseholdClaimInfoComponent,
    HouseholdMetadataComponent,
    IconRendererComponent,
    LanguageSwitcherComponent,
    LeadDetailsComponent,
    LegalProtectionClaimInfoComponent,
    LegalProtectionMetadataComponent,
    LinearStepperComponent,
    PersonalClaimDetailsComponent,
    PolicyUpdateMetadataComponent,
    ProposalDetailsComponent,
    RcCompanyMetadataComponent,
    ReplaceUnderscorePipe,
    SafeHtmlPipe,
    SelectAllCheckboxComponent,
    SelectAllMatCheckboxComponent,
    SharedBusinessMetadataComponent,
    SlideUpModalComponent,
    SpeedometerChartComponent,
    SplitPipe,
    StackedDateRendererComponent,
    StackedTextRendererComponent,
    ThirdPillarMetadataComponent,
    TravelClaimInfoComponent,
    TravelMetadataComponent,
    UpdateDocumentModalComponent,
    ValuableObjectsClaimInfoComponent,
    ValuableObjectsMetadataComponent,
    VehicleClaimInfoComponent,
    VehicleMetadataComponent,
    ViewProposalsComponent,
  ],
  imports: [
    ActionModalComponent,
    AddLeadModalComponent,
    AddProposalModalComponent,
    AgGridModule,
    AnimalFormComponent,
    BarChartComponent,
    BaseLayoutComponent,
    BenefitsAccountFormComponent,
    BuildingFormComponent,
    BusinessAccidentFormComponent,
    BusinessConstructionFormComponent,
    BusinessConstructionWarrantyFormComponent,
    BusinessCyberFormComponent,
    BusinessFleetFormComponent,
    BusinessHealthFormComponent,
    BusinessLegalProtectionFormComponent,
    BusinessRentalFormComponent,
    BusinessRetirementFormComponent,
    BusinessTechnicalInstallationFormComponent,
    BusinessThingsFormComponent,
    BusinessTransportFormComponent,
    BusinessVehicleFormComponent,
    CentralisationFormComponent,
    ComboboxComponent,
    CommonModule,
    ConstructionFormComponent,
    CustomDatePipe,
    DocumentUploadModalComponent,
    EditorModule,
    FileInputComponent,
    FileSizePipe,
    FilterTabsComponent,
    FormsModule,
    HealthFormComponent,
    HousingRentalFormComponent,
    IconsModule,
    LeadFormComponent,
    LegalProtectionFormComponent,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatTreeModule,
    NgxMatIntlTelInputComponent,
    PdfViewerModalComponent,
    PdfViewerModule,
    ProductSelectionComponent,
    ProposalProductsDisplayerComponent,
    ProposalsActionsComponent,
    RcCompanyFormComponent,
    RcHouseholdFormComponent,
    ReactiveFormsModule,
    SendProposalsModalComponent,
    SharedBusinessFormComponent,
    SharedRoutingModule,
    SlideInModalComponent,
    TabNavigationComponent,
    TagInputModule,
    TasksListComponent,
    ThirdPillarFormComponent,
    ToggleComponent,
    TravelFormComponent,
    TreeComponent,
    ValuableObjectsFormComponent,
    VehicleFormComponent,
  ],
  exports: [
    ActionModalComponent,
    ActionModalComponent,
    AddClaimModalComponent,
    AddLeadModalComponent,
    AddProposalModalComponent,
    AgentActivationModalComponent,
    AnimalMetadataComponent,
    BarChartComponent,
    BarDistributionChartComponent,
    BaseLayoutComponent,
    BuildingMetadataComponent,
    CancellationMetadataComponent,
    CentralisationMetadataComponent,
    ClaimDetailsComponent,
    CollaboratorFormComponent,
    ComboboxComponent,
    CommissionBaseListComponent,
    ConstructionMetadataComponent,
    CustomDatePipe,
    customerFilter,
    CustomerNavComponent,
    CustomerSourceSelectComponent,
    DataAnalysisNavComponent,
    DateTimePipe,
    DocumentRequestModalComponent,
    DocumentUploadModalComponent,
    DonutChartComponent,
    DuplicateLeadComponent,
    DuplicateLeadComponent,
    FileDragDropDirective,
    FileInputComponent,
    FileSizePipe,
    FilterTabsComponent,
    FloatingButtonsComponent,
    GenerateUserPasswordModalComponent,
    GoogleAutocompleteComponent,
    GridCopyButtonComponent,
    HealthMetadataComponent,
    HouseholdMetadataComponent,
    IconsModule,
    LanguageSwitcherComponent,
    LeadDetailsComponent,
    LegalProtectionMetadataComponent,
    LinearStepperComponent,
    PdfViewerModalComponent,
    ProductSelectionComponent,
    ProposalsActionsComponent,
    ReplaceUnderscorePipe,
    SafeHtmlPipe,
    SelectAllCheckboxComponent,
    SelectAllMatCheckboxComponent,
    SendProposalsModalComponent,
    SlideInModalComponent,
    SlideInModalComponent,
    SlideUpModalComponent,
    SpeedometerChartComponent,
    SplitPipe,
    TabNavigationComponent,
    ThirdPillarMetadataComponent,
    ToggleComponent,
    TravelMetadataComponent,
    TreeComponent,
    UpdateDocumentModalComponent,
    ValuableObjectsMetadataComponent,
    VehicleMetadataComponent,
    ViewProposalsComponent,
  ],
  providers: [
    DatePipe,
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation],
    },
  ],
})
export class SharedModule {}
